import React, { useContext } from 'react'
import styled from 'styled-components'

// Components
import { RowAligned } from '../layout/LayoutAtoms'
import Text from '../Text'
import LayoverModal, { LayoverModalContext } from '../layout/LayoverModal'
import Button from '../Button'
import { SearchInput } from '../TeamFilters'
import Dropdown, { DropdownWrapper } from '../formControl/Dropdown'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import AvatarHeader, { IProps as AvatarHeaderProps } from '../AvatarHeader'

interface HeaderConfig {
  title?: string
  subtitle?: string
  leftContent?: React.ReactNode
  onBack?: () => void
  secondButton?: React.ReactNode
  cta?: AvatarHeaderProps
}

interface IDropdown {
  type: any
  label?: string
  options: IOption[]
}

interface ModalProps {
  headerConfig: HeaderConfig
  closeModal: () => void
  searchPlaceholder?: string
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>
  dropdowns?: IDropdown[]
  transformDropdown?: boolean
  setDropdowns?: React.Dispatch<React.SetStateAction<IDropdown[] | undefined>>
  children: React.ReactNode
  onClick?: () => void
  className?: string
  // Forwarded to LayoverModal: when true, closing prompts to discard changes.
  isDirty?: boolean
}

export interface IOption<T = any> {
  value: string
  label: string
  selected: boolean
  disabled?: boolean
  results: T
}

const StickyHeaderLayoverModal: React.FC<ModalProps> = ({
  headerConfig,
  dropdowns,
  transformDropdown,
  closeModal,
  setSearchTerm,
  searchPlaceholder,
  setDropdowns,
  className,
  children,
  onClick,
  isDirty,
}) => {
  return (
    <StyledLayoverModal
      className={className}
      closeModal={closeModal}
      onClick={onClick}
      isDirty={isDirty}
      content={
        <>
          <StickyHeader>
            <Header>
              <div>
                {headerConfig.subtitle && (
                  <Text
                    textStyle="labelSmallCaps"
                    color="text72"
                    marginBottom={4}
                  >
                    {headerConfig.subtitle}
                  </Text>
                )}
                <StyledRowAligned>
                  <HeaderBackButton onBack={headerConfig.onBack} />
                  {headerConfig.cta && <AvatarHeader {...headerConfig.cta} />}
                  {headerConfig.leftContent}
                  {headerConfig.title && (
                    <Text textStyle="displayS">{headerConfig.title}</Text>
                  )}
                </StyledRowAligned>
              </div>
              <HeaderActions>
                {headerConfig.secondButton}
                <CloseButton closeModal={closeModal} />
              </HeaderActions>
            </Header>
            {setSearchTerm && (
              <StyledSearchInput
                placeholder={searchPlaceholder}
                setSearchTerm={setSearchTerm}
              />
            )}

            {dropdowns && setDropdowns && (
              <Filters width={Math.floor(100 / (dropdowns?.length || 2))}>
                {dropdowns?.map((dropdown, index) =>
                  transformDropdown ? (
                    <ButtonsContainer key={`buttons-${index}`}>
                      {dropdown.options.map((option) => (
                        <Button
                          key={`button-${index}-${option.value}`}
                          caption={option.label}
                          colorVariant={
                            option.selected ? 'primary' : 'secondary'
                          }
                          onClick={() => {
                            setDropdowns(
                              dropdowns.map((dr, i) =>
                                i === index
                                  ? {
                                      ...dr,
                                      options: dr.options.map((op) => ({
                                        ...op,
                                        selected: op.value === option.value,
                                      })),
                                    }
                                  : dr
                              )
                            )
                          }}
                          disabled={option.disabled}
                        />
                      ))}
                    </ButtonsContainer>
                  ) : (
                    <Dropdown
                      key={`feedback-dropdown-${index}`}
                      coloredBackground
                      label={dropdown.label}
                      value={
                        dropdown.options.find((op) => op.selected)?.value ||
                        dropdown.options[0]?.value
                      }
                      onChange={(e) => {
                        const selectedOption = dropdown.options.find(
                          (op) => op.value === e.target.value
                        )
                        setDropdowns(
                          dropdowns.map((dr, i) => {
                            if (i === index) {
                              return {
                                ...dr,
                                options: dr.options.map((op) => ({
                                  ...op,
                                  selected: op.value === selectedOption?.value,
                                })),
                              }
                            } else {
                              return dr
                            }
                          })
                        )
                      }}
                    >
                      {dropdown.options.map((option) => (
                        <option
                          value={option.value}
                          key={`dropdown-${index}-${option.value}`}
                          disabled={option.disabled}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Dropdown>
                  )
                )}
              </Filters>
            )}
          </StickyHeader>
          {children}
        </>
      }
    />
  )
}

export default StickyHeaderLayoverModal

// Rendered inside LayoverModal's content, so it picks up the stack context.
// The X closes the entire stack; falls back to the raw prop when standalone.
const CloseButton: React.FC<{ closeModal: () => void }> = ({ closeModal }) => {
  const ctx = useContext(LayoverModalContext)
  return (
    <Button
      icon="Close"
      colorVariant="secondary"
      onClick={ctx?.closeAll ?? closeModal}
    />
  )
}

// Back pops only the top layer (animated). Shown when the consumer provides an
// onBack, or automatically when this layover is stacked on top of another.
const HeaderBackButton: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const ctx = useContext(LayoverModalContext)
  const show = onBack || (ctx && !ctx.isBottom)
  if (!show) return null
  return (
    <BackButton
      icon="ChevronLeft"
      colorVariant="secondary"
      onClick={onBack ?? ctx?.requestClose}
    />
  )
}

const StyledLayoverModal = styled(LayoverModal)`
  /* Layover > ShiftWrapper > Modal — zero the panel's top padding so the sticky
     header sits flush (the extra div is the depth-shift wrapper). */
  > div > div {
    padding-top: 0;
  }
`

export const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  /* Must outrank the Dropdown's <select> (z-index: 4) so body fields scroll
     behind the pinned header instead of over it. */
  z-index: 5;
  background-color: ${({ theme }) => theme.colors.lightest};
  padding-top: 16px;

  ${mqFrom.M`
    padding-top: 40px;
  `}
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`

// Keeps the consumer's action clear of the close (X) button, so no consumer has
// to add that margin itself.
const HeaderActions = styled(RowAligned)`
  gap: 12px;
  flex-shrink: 0;
`

const BackButton = styled(Button)`
  margin-right: 12px;

  ${mqFrom.M`
    margin-right: 24px;
  `}
`
const StyledSearchInput = styled(SearchInput)`
  > div {
    margin-bottom: 12px;
  }
`

const Filters = styled(RowAligned)<{ width: number }>`
  flex-wrap: wrap;
  justify-content: space-between;

  ${DropdownWrapper} {
    padding-bottom: 12px;

    ${mqTo.M`
      width: 100%;
    `}

    ${mqFrom.M`
      ${({ width }) =>
        width &&
        `
        width: ${
          width === 50 ? `calc(${width}% - 6px)` : `calc(${width}% - 8px)`
        };
      `}
    `}
  }
`

const ButtonsContainer = styled(RowAligned)`
  gap: 8px;
  margin-bottom: 16px;
`

const StyledRowAligned = styled(RowAligned)`
  flex-wrap: wrap;
`
